import { reactive, computed } from "vue";
import { parseNumericCell } from "../helpers/numericParser.mjs";

const state = reactive({
  cells: new Map(),
  anchor: null,
  dragging: false,
  dragStart: null,
  wasDragging: false,
});

const keyOf = (cell) => `${cell.tableUid}:${cell.rowIdx}:${cell.colIdx}`;

function addCell(cell) {
  const key = keyOf(cell);
  if (state.cells.has(key)) return;
  const parsed = parseNumericCell(cell.rawText);
  state.cells.set(key, {
    el: cell.el,
    tableUid: cell.tableUid,
    rowIdx: cell.rowIdx,
    colIdx: cell.colIdx,
    rawText: cell.rawText,
    value: parsed.value,
    isNumeric: parsed.isNumeric,
  });
  if (cell.el && cell.el.classList) cell.el.classList.add("cell-selected");
}

function removeCell(cell) {
  const key = keyOf(cell);
  const rec = state.cells.get(key);
  if (!rec) return;
  if (rec.el && rec.el.classList) rec.el.classList.remove("cell-selected");
  state.cells.delete(key);
}

function toggleCell(cell) {
  if (state.cells.has(keyOf(cell))) removeCell(cell);
  else addCell(cell);
}

function clearSelection() {
  for (const rec of state.cells.values()) {
    if (rec.el && rec.el.classList) rec.el.classList.remove("cell-selected");
  }
  state.cells.clear();
  state.anchor = null;
  state.dragging = false;
  state.dragStart = null;
}

function setAnchor(anchor) {
  state.anchor = anchor;
}

function replaceWithRange(cells) {
  clearSelection();
  for (const c of cells) addCell(c);
}

const stats = computed(() => {
  const count = state.cells.size;
  let sum = 0, min = Infinity, max = -Infinity, numericCount = 0;
  for (const rec of state.cells.values()) {
    if (!rec.isNumeric) continue;
    numericCount++;
    sum += rec.value;
    if (rec.value < min) min = rec.value;
    if (rec.value > max) max = rec.value;
  }
  if (numericCount === 0) {
    return { count, numericCount: 0, sum: null, avg: null, min: null, max: null };
  }
  return {
    count,
    numericCount,
    sum,
    avg: sum / numericCount,
    min,
    max,
  };
});

export function useCellSelection() {
  return {
    state,
    stats,
    addCell,
    removeCell,
    toggleCell,
    clearSelection,
    setAnchor,
    replaceWithRange,
  };
}
